import React from 'react';
import PropTypes from 'prop-types';
import { InputAdornment, MenuItem, Stack, Switch, TextField } from '@mui/material';
import ConfigCard from './ConfigCard';
import { PdiRow } from './_PdiHelpers';

// DETAILS_COLOR_DISPLAY: 'image' | 'color' — the two the storefront's variant
// selector renders; with any other value it shows no variants at all.
const VARIANT_DISPLAYS = [
    { value: 'colorImage',  i18nKey: 'cfg_pdi_related_variants_color_image', fallback: 'Product image'               },
    { value: 'colorChips',  i18nKey: 'cfg_pdi_related_variants_chips',       fallback: 'Color dot with the color name' },
];

const RelatedProductsCard = ({ value = {}, onChange, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const get = (k, fb) => (value[k] !== undefined ? value[k] : fb);
    const set = (k) => (v) => onChange && onChange(k, v);

    return (
        <ConfigCard
            anchor="cfg-card-pdi-related"
            title={tr('cfg_pdi_related_title', 'Related products & variants')}
            description={tr('cfg_pdi_related_desc', 'How related items and product variants behave.')}
            theme={theme}
        >
            <Stack spacing={1.25} divider={<div style={{ borderTop: '1px solid rgba(0,0,0,0.08)' }} />}>
                <PdiRow
                    label={tr('cfg_pdi_related_max', 'Maximum related products to show')}
                    control={
                        <TextField
                            type="number" size="small"
                            value={get('maxToShow', 99) ?? 99}
                            onChange={(e) => set('maxToShow')(Math.max(0, parseInt(e.target.value) || 0))}
                            InputProps={{ endAdornment: <InputAdornment position="end">{tr('cfg_pdi_related_products_unit', 'products')}</InputAdornment> }}
                            sx={{ width: 160 }}
                        />
                    }
                />
                <PdiRow
                    label={tr('cfg_pdi_related_variants_label', 'How variants are displayed')}
                    help={tr('cfg_pdi_related_variants_help', 'Choose how related variants of a product look')}
                    control={
                        <TextField
                            select size="small"
                            value={get('variantsDisplay', 'colorImage') || 'colorImage'}
                            onChange={(e) => set('variantsDisplay')(e.target.value)}
                            sx={{ minWidth: 220 }}
                        >
                            {VARIANT_DISPLAYS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>{tr(opt.i18nKey, opt.fallback)}</MenuItem>
                            ))}
                        </TextField>
                    }
                />
                <PdiRow
                    label={tr('cfg_pdi_related_show_oos', 'Show out-of-stock variants')}
                    help={tr('cfg_pdi_related_show_oos_help', 'Display variants that are no longer available')}
                    control={
                        <Switch
                            color="success"
                            size="small"
                            checked={!!get('showOutOfStock')}
                            onChange={(e) => set('showOutOfStock')(e.target.checked)}
                        />
                    }
                />
                <PdiRow
                    label={tr('cfg_pdi_related_highlight', 'Highlight the selected variant with a border')}
                    control={
                        <Switch
                            color="success"
                            size="small"
                            checked={!!get('highlightSelected')}
                            onChange={(e) => set('highlightSelected')(e.target.checked)}
                        />
                    }
                />
            </Stack>
        </ConfigCard>
    );
};

RelatedProductsCard.propTypes = {
    value:    PropTypes.object,
    onChange: PropTypes.func,
    theme:    PropTypes.string,
    t:        PropTypes.func,
};

export default RelatedProductsCard;
